import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { callApi, getInlineLoader } from "../Helpers";
import { FiUser, FiMail, FiPhone, FiMapPin, FiCalendar, FiSave, FiArrowLeft } from "react-icons/fi";

export default function EditApplicantProfile() {
  const appContext = useContext(AppContext);

  /* Core Lifecycle States */
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  /* Working Controlled Form Input States */
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [physicalAddress, setPhysicalAddress] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("Male");

  // Initial load execution lifecycle
  useEffect(() => {
    const init = async () => {
      setReady(false);
      try {
        const response = await callApi('jobapplicant/get-current-applicant-data', {});
        if (response && response.status === 1) {
          setData(response.data);
        } else {
          appContext.tellError('Could not fetch profile records');
        }
      } catch (err) {
        appContext.tellError('Network error connecting to registry server');
      }
      setReady(true);
    };
    init();
  }, []);

  // Data mapping loop execution logic
  useEffect(() => {
    if (data) {
      setFirstName(data.firstName || "");
      setMiddleName(data.middleName || "");
      setLastName(data.lastName || "");
      setEmail(data.email || "");
      setPhone(data.phone || "");
      setPhysicalAddress(data.physicalAddress || "");
      setGender(data.gender || "Male");
      
      if (data.dob) {
        setDob(data.dob.split("T")[0]);
      }
    }
  }, [data]);

  useEffect(() => {
    appContext.setShowOverlayLoader(loading);
    return () => appContext.setShowOverlayLoader(false);
  }, [loading]);

  async function handleUpdate() {
    // Polished unique validation constraints
    if (!firstName.trim()) return appContext.tellError("First name field is required.");
    if (!middleName.trim()) return appContext.tellError("Middle name is required.");
    if (!lastName.trim()) return appContext.tellError("Last name field is required.");
    if (!phone.trim()) return appContext.tellError("Mobile phone number is required.");
    if (!physicalAddress.trim()) return appContext.tellError("Physical Address is required.");
    if (!dob.trim()) return appContext.tellError("Date of Birth is required.");
    if (!gender.trim()) return appContext.tellError("Select Gender.");

    setLoading(true);
    const payload = {
      firstName,
      middleName,
      lastName,
      phone,
      physicalAddress,
      dob,
      gender
    };

    const response = await callApi('jobapplicant/update-account-details', payload);
    if (response && response.status === 1) {
      appContext.tellMessage('Profile records updated successfully.');
      appContext.navBack(); // 👈 Smoothly returns to previous history panel stack entry
    } else {
      appContext.tellError(response.msg || "Failed to update profile data.");
    }
    setLoading(false);
  }

  if (!ready) return <div className="container py-5 text-center">{getInlineLoader()}</div>;

  return (
    <>
      <style>{`
        .profile-panel { font-family: 'Inter', sans-serif; padding: 24px; background: #ffffff; min-height: 100vh; }
        .profile-field-group { margin-bottom: 20px; }
        .profile-label { font-size: 0.8rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; display: block; }
        .profile-input-wrapper { display: flex; align-items: center; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 2px 14px; }
        .profile-input-wrapper:focus-within { border-color: #0d9488; background: #ffffff; }
        .profile-icon { color: #64748b; margin-right: 12px; display: flex; align-items: center; }
        .profile-input { border: none; background: transparent; width: 100%; padding: 12px 0; font-size: 0.95rem; outline: none; color: #0f172a; }
        .profile-input:disabled { cursor: not-allowed; opacity: 0.6; }
      `}</style>

      <div className="profile-panel">
        <div className="border-bottom pb-3 mb-4 d-flex align-items-center gap-3">
          <button className="btn btn-link p-0 text-dark" onClick={() => appContext.navBack()} style={{ textDecoration: 'none' }}>
            <FiArrowLeft size={20} />
          </button>
          <div>
            <h4 className="fw-bold mb-0" style={{ color: '#0f172a' }}>Profile Settings</h4>
            <p className="text-muted small mb-0">Modify your verified background information files details.</p>
          </div>
        </div>

        <div className="row">
          {/* First Name */}
          <div className="col-12 profile-field-group">
            <label className="profile-label">First Name</label>
            <div className="profile-input-wrapper">
              <span className="profile-icon"><FiUser size={16} /></span>
              <input type="text" className="profile-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
          </div>

          {/* Middle Name */}
          <div className="col-12 profile-field-group">
            <label className="profile-label">Middle Name</label>
            <div className="profile-input-wrapper">
              <span className="profile-icon"><FiUser size={16} /></span>
              <input type="text" className="profile-input" value={middleName} onChange={(e) => setMiddleName(e.target.value)} />
            </div>
          </div>

          {/* Last Name */}
          <div className="col-12 profile-field-group">
            <label className="profile-label">Last Name</label>
            <div className="profile-input-wrapper">
              <span className="profile-icon"><FiUser size={16} /></span>
              <input type="text" className="profile-input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>

          {/* Email (Disabled/Locked) */}
          <div className="col-12 profile-field-group">
            <label className="profile-label">Email Account Address</label>
            <div className="profile-input-wrapper" style={{ background: '#f1f5f9' }}>
              <span className="profile-icon"><FiMail size={16} /></span>
              <input type="email" className="profile-input" value={email} disabled />
            </div>
          </div>

          {/* Phone */}
          <div className="col-12 profile-field-group">
            <label className="profile-label">Mobile Phone Number</label>
            <div className="profile-input-wrapper">
              <span className="profile-icon"><FiPhone size={16} /></span>
              <input type="text" className="profile-input" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
          </div>

          {/* Physical Address */}
          <div className="col-12 profile-field-group">
            <label className="profile-label">Physical Residential Address</label>
            <div className="profile-input-wrapper">
              <span className="profile-icon"><FiMapPin size={16} /></span>
              <input type="text" className="profile-input" value={physicalAddress} onChange={(e) => setPhysicalAddress(e.target.value)} />
            </div>
          </div>

          {/* Date of Birth */}
          <div className="col-12 profile-field-group">
            <label className="profile-label">Date of Birth</label>
            <div className="profile-input-wrapper">
              <span className="profile-icon"><FiCalendar size={16} /></span>
              <input type="date" className="profile-input" value={dob} onChange={(e) => setDob(e.target.value)} />
            </div>
          </div>

          {/* Gender */}
          <div className="col-12 profile-field-group">
            <label className="profile-label">Gender</label>
            <div className="profile-input-wrapper">
              <select className="profile-input" value={gender} onChange={(e) => setGender(e.target.value)}>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
          </div>

          {/* Submit Trigger */}
          <div className="col-12 mt-4">
            <button 
              className="btn w-100 py-3 text-white fw-bold d-flex align-items-center justify-content-center gap-2" 
              style={{ background: '#0f172a', borderRadius: '8px', border: 'none' }} 
              onClick={handleUpdate}
              disabled={loading}
            >
              <FiSave /> {loading ? "SAVING..." : "UPDATE RECORDS"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}